import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { of } from 'rxjs';
import { catchError, finalize, switchMap, take } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { AuthService } from '../../auth/auth.service';
import { SessionStorageService } from '../../helpers/session-storage.service';
import { Customer } from '../customer.model';
import { CustomerService } from '../customer.service';
import { LocationSearchComponent } from '../../shared/location-search/location-search.component';
import { SharedEndpointService } from '../../services/shared-endpoint.service';
import { Country } from '../../models/common/country.model';

@Component({
  selector: 'app-customer-edit',
  standalone: true,
  templateUrl: './customer-edit.component.html',
  styleUrls: ['../../shared/management-pages.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    ButtonModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    SelectModule,
    LocationSearchComponent,
    ToastModule
  ],
  providers: [MessageService]
})
export class CustomerEditComponent {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly customerService = inject(CustomerService);
  private readonly authService = inject(AuthService);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly messageService = inject(MessageService);
  private readonly sharedEndpointService = inject(SharedEndpointService);

  customer = signal<Customer | null>(null);
  countries = signal<Country[]>([]);
  isProfile = signal(false);
  isLoading = signal(false);
  isSaving = signal(false);
  error = signal<string | null>(null);

  form = this.fb.group({
    searchLocation: [''],
    name: ['', Validators.required],
    imageUrl: [''],
    address: [''],
    dateOfBirth: [''],
    gender: [''],
    countryId: [null as number | null],
    corporateRole: [''],
    firebaseId: [{ value: '', disabled: true }],
    emailAddress: [{ value: '', disabled: true }],
    phoneNumber: [{ value: '', disabled: true }],
    isPhoneVerified: [{ value: '', disabled: true }],
    companyName: [{ value: '', disabled: true }],
    rolesText: [{ value: '', disabled: true }]
  });

  ngOnInit(): void {
    this.loadCountries();
    this.loadCustomer();
  }

  placeSelect(place: google.maps.places.Place): void {
    const countryCode = this.readCountryCode(place);
    if (countryCode) {
      this.sharedEndpointService.findCountryByCode(countryCode, 'en-US').subscribe({
        next: country => this.form.patchValue({ countryId: country?.id ?? this.form.getRawValue().countryId }),
        error: () => undefined
      });
    }

    this.form.patchValue({
      name: place.displayName ?? this.form.getRawValue().name,
      address: place.formattedAddress ?? this.form.getRawValue().address,
      imageUrl: place.photos?.[0]?.getURI() ?? this.form.getRawValue().imageUrl
    });
  }

  loadCustomer(): void {
    const id = this.route.snapshot.paramMap.get('id');
    this.isProfile.set(!id);
    this.isLoading.set(true);
    this.error.set(null);

    const request$ = id
      ? this.customerService.getCustomerById(Number(id))
      : this.authService.userData.pipe(
          take(1),
          switchMap(user => {
            const sessionCustomer = this.sessionStorage.getCustomer();
            const firebaseId = user?.uid ?? sessionCustomer?.firebaseId;
            return firebaseId ? this.customerService.getCustomerByFirebaseId(firebaseId) : of(null);
          })
        );

    request$.pipe(
      finalize(() => this.isLoading.set(false)),
      catchError(error => {
        this.error.set(error?.message || 'Unable to load customer.');
        return of(null);
      })
    ).subscribe(response => {
      const customer = response?.data ?? null;
      this.customer.set(customer);
      if (!customer) {
        this.error.set('Customer profile was not found.');
        return;
      }

      this.form.patchValue({
        name: customer.name,
        imageUrl: customer.imageUrl,
        address: customer.address,
        dateOfBirth: this.toDateInputValue(customer.dateOfBirth),
        gender: customer.gender === true ? 'true' : customer.gender === false ? 'false' : '',
        countryId: customer.countryId,
        corporateRole: customer.corporateRole,
        firebaseId: customer.firebaseId,
        emailAddress: customer.emailAddress,
        phoneNumber: customer.phoneNumber,
        isPhoneVerified: customer.isPhoneVerified ? 'Yes' : 'No',
        companyName: customer.company?.name ?? '',
        rolesText: (customer.roles ?? []).map(role => role.roleName).join(', ')
      });
    });
  }

  save(): void {
    const original = this.customer();
    if (!original) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const payload: Customer = {
      ...original,
      name: value.name ?? '',
      imageUrl: value.imageUrl ?? '',
      address: value.address ?? '',
      dateOfBirth: value.dateOfBirth ? new Date(value.dateOfBirth) : original.dateOfBirth,
      gender: value.gender === 'true' ? true : value.gender === 'false' ? false : original.gender,
      countryId: value.countryId ?? original.countryId,
      corporateRole: value.corporateRole ?? original.corporateRole,
      firebaseId: original.firebaseId,
      emailAddress: original.emailAddress,
      phoneNumber: original.phoneNumber,
      isPhoneVerified: original.isPhoneVerified,
      company: original.company,
      roles: original.roles
    };

    this.isSaving.set(true);
    this.customerService.updateCustomer(payload).pipe(
      finalize(() => this.isSaving.set(false))
    ).subscribe({
      next: response => {
        if (!response.success) {
          this.messageService.add({ severity: 'error', summary: 'Save failed', detail: response.message });
          return;
        }
        if (this.isProfile()) {
          this.sessionStorage.saveCustomer(response.data);
        }
        this.messageService.add({ severity: 'success', summary: 'Customer saved', detail: response.message });
        this.router.navigate(this.backLink());
      },
      error: error => this.messageService.add({
        severity: 'error',
        summary: 'Save failed',
        detail: error?.message || 'Unable to save customer.'
      })
    });
  }

  backLink(): string[] {
    const current = this.customer();
    return this.isProfile() ? ['/profile'] : ['/company/employees', String(current?.id ?? '')];
  }

  private toDateInputValue(value: Date | string | null | undefined): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toISOString().slice(0, 10);
  }

  private loadCountries(): void {
    this.sharedEndpointService.findAllCountries('en-US').subscribe({
      next: countries => this.countries.set(countries ?? []),
      error: error => this.messageService.add({
        severity: 'warn',
        summary: 'Countries unavailable',
        detail: error?.message || 'Unable to load country list.'
      })
    });
  }

  private readCountryCode(place: google.maps.places.Place): string | null {
    const country = place.addressComponents?.find(component => component.types.includes('country'));
    return country?.shortText ?? (country as any)?.short_name ?? null;
  }
}
